import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSlice } from "@bridge-applications/provider-kit";
import coconutWalkMusic from "../../../assets/static/audio/music/core-loop/core-loop__coconut-walk.m4a";
import { setSoundEffectsEnabled as configureSoundEffects } from "../../../logic/helpers/audio";
import {
    BACKGROUND_MUSIC_FADE_OUT_DURATION_MS,
    IS_SLOT_MACHINE_BACKGROUND_MUSIC_AVAILABLE,
} from "../../../state/audio";

const SLOT_MACHINE_PATH = "/";
const MUSIC_VOLUME = 0.2;

interface FadeOptions {
    targetVolume: number;
    durationMs: number;
    onComplete?: () => void;
}

function fadeAudioVolume(
    audio: HTMLAudioElement,
    { targetVolume, durationMs, onComplete }: FadeOptions,
): () => void {
    const initialVolume = audio.volume;
    const startedAt = performance.now();
    let animationFrameId = 0;

    const updateVolume = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / durationMs);
        audio.volume =
            initialVolume + (targetVolume - initialVolume) * progress;

        if (progress < 1) {
            animationFrameId = window.requestAnimationFrame(updateVolume);
            return;
        }

        onComplete?.();
    };

    animationFrameId = window.requestAnimationFrame(updateVolume);
    return () => window.cancelAnimationFrame(animationFrameId);
}

export function BackgroundMusicManager() {
    const { pathname } = useLocation();
    const { state: audioSettings } = useSlice("audioSettingsSlice");
    const isSlotMachineMusicEnabled =
        IS_SLOT_MACHINE_BACKGROUND_MUSIC_AVAILABLE &&
        audioSettings.slotMachineMusicEnabled;
    const shouldPlay =
        pathname === SLOT_MACHINE_PATH && isSlotMachineMusicEnabled;
    const audioRef = useRef<HTMLAudioElement>(null);
    const cancelFadeRef = useRef<() => void>(() => undefined);
    const shouldPlayRef = useRef(shouldPlay);

    const fadeTo = useCallback(
        (
            audio: HTMLAudioElement,
            targetVolume: number,
            durationMs: number,
            onComplete?: () => void,
        ) => {
            cancelFadeRef.current();
            cancelFadeRef.current = fadeAudioVolume(audio, {
                targetVolume,
                durationMs,
                onComplete,
            });
        },
        [],
    );

    const startMusic = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || !shouldPlayRef.current) return;

        cancelFadeRef.current();
        audio.volume = MUSIC_VOLUME;
        if (!audio.paused) return;

        void audio
            .play()
            .then(() => {
                if (!shouldPlayRef.current) {
                    audio.pause();
                    audio.currentTime = 0;
                }
            })
            .catch(() => {
                // Mobile browsers may require a user gesture. The document
                // listeners below retry while this route still wants music.
            });
    }, []);

    useEffect(() => {
        configureSoundEffects(audioSettings.soundEffectsEnabled);
    }, [audioSettings.soundEffectsEnabled]);

    useEffect(() => {
        shouldPlayRef.current = shouldPlay;

        const audio = audioRef.current;
        if (!audio) return;

        if (shouldPlay) {
            startMusic();
            return;
        }

        cancelFadeRef.current();
        if (!isSlotMachineMusicEnabled) {
            audio.pause();
            audio.currentTime = 0;
            audio.volume = 0;
            return;
        }

        if (audio.paused) {
            audio.currentTime = 0;
            audio.volume = 0;
            return;
        }

        fadeTo(audio, 0, BACKGROUND_MUSIC_FADE_OUT_DURATION_MS, () => {
            if (shouldPlayRef.current) return;
            audio.pause();
            audio.currentTime = 0;
        });
    }, [fadeTo, isSlotMachineMusicEnabled, shouldPlay, startMusic]);

    useEffect(() => {
        const retryBlockedPlayback = () => {
            if (audioRef.current?.paused) startMusic();
        };

        document.addEventListener("pointerdown", retryBlockedPlayback, {
            capture: true,
        });
        document.addEventListener("keydown", retryBlockedPlayback, {
            capture: true,
        });

        return () => {
            document.removeEventListener("pointerdown", retryBlockedPlayback, {
                capture: true,
            });
            document.removeEventListener("keydown", retryBlockedPlayback, {
                capture: true,
            });
            cancelFadeRef.current();
            audioRef.current?.pause();
        };
    }, [startMusic]);

    return (
        <audio
            ref={audioRef}
            src={
                IS_SLOT_MACHINE_BACKGROUND_MUSIC_AVAILABLE
                    ? coconutWalkMusic
                    : undefined
            }
            loop
            preload={
                IS_SLOT_MACHINE_BACKGROUND_MUSIC_AVAILABLE ? "auto" : "none"
            }
            aria-hidden="true"
        />
    );
}
